import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'projects/foreign-admits/src/environments/environment';
import { ApiHelperService } from 'projects/apna-lib/src/lib/services/api-helper.service';
import { forkJoin, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';

@Component({
  selector: 'fa-compare-now',
  templateUrl: './compare-now.component.html',
  styleUrls: ['./compare-now.component.scss']
})
export class CompareNowComponent implements OnInit {
  private baseUrl: string = environment.apiBaseUrl;
  courses: any[] = [];
  courseIds: string[] = [];
  puid: string | null = null;
  isLoading: boolean = true;
  errorMessage: string = '';

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private http: HttpClient,
    private apiHelperService: ApiHelperService
  ) { }

  ngOnInit(): void {
    console.log('CompareNowComponent initialized');
    
    // Get PUID from route params
    this.puid = this.route.snapshot.paramMap.get('puid');
    console.log('PUID from route:', this.puid);
    
    // Get course IDs from query parameters
    this.route.queryParams.subscribe(params => {
      console.log('Query params received:', params);
      const coursesParam = params['courses'];
      
      if (coursesParam) {
        this.courseIds = coursesParam.split(',').filter((id: string) => id.trim() !== '');
        console.log('Parsed course IDs:', this.courseIds);
        
        if (this.courseIds.length > 0) {
          // Small delay to ensure UI is ready
          setTimeout(() => {
            this.fetchAllCourseDetails();
          }, 100);
        } else {
          console.error('No valid course IDs found after parsing');
          this.errorMessage = 'No valid course IDs provided';
          this.goBack();
        }
      } else {
        console.error('No courses parameter found in query string');
        this.errorMessage = 'No courses selected for comparison';
        setTimeout(() => this.goBack(), 1000);
      }
    });
  }

  fetchAllCourseDetails(): void {
    if (this.courseIds.length === 0) {
      this.goBack();
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';

    // Create an array of HTTP observables for each course ID
    const courseRequests = this.courseIds.map(courseId => 
      this.fetchCourseDetails(courseId)
    );

    // Use forkJoin to execute all requests in parallel
    forkJoin(courseRequests).subscribe(
      (responses: any[]) => {
        this.courses = responses.filter(course => course !== null);
        console.log('All courses fetched:', this.courses);
        this.isLoading = false;

        // If no courses were successfully fetched, go back
        if (this.courses.length === 0) {
          this.errorMessage = 'No course details could be loaded.';
          setTimeout(() => this.goBack(), 2000);
        }
      },
      (error) => {
        console.error('Error fetching course details:', error);
        this.errorMessage = 'Failed to load course details.';
        this.isLoading = false;
        setTimeout(() => this.goBack(), 2000);
      }
    );
  }

  fetchCourseDetails(courseId: string): any {
  console.log(`Fetching details for course ID: ${courseId}`);
  
  const token = localStorage.getItem('token');
  if (!token) {
    console.error('No access token found');
    return of(null);
  }

  const headers = new HttpHeaders({
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  });

  return this.apiHelperService.getDataWithShortlistingUrl(`university-courses/course-details/${courseId}`).then(
    (response: any) => {
      console.log(`API response for course ${courseId}:`, response);
      
      if (response && response.success && response.data) {
        const { university, country, loan_offers, vendors, courseDetails, qs_ranking } = response.data;

        let transformedLoanOffers = vendors;
        if (typeof vendors === 'string' && vendors !== 'No' && vendors !== 'N/A') {
          if (vendors.includes(',')) {
            transformedLoanOffers = vendors.split(',').map(offer => offer.trim());
          } else if (vendors === 'Yes') {
            transformedLoanOffers = vendors;
          }
        }
        
        const transformedCourse = {
          courseId: courseId,
          course_id: courseDetails.course_id || courseId,
          course_name: courseDetails.course_name || 'Course name not available',
          university: university || 'University not available',
          country: country || 'Country not available',
          yearly_tuition_fees: courseDetails.yearly_tuition_fees || 0,
          currency: courseDetails.currency || 'USD',
          application_fee: courseDetails.application_fee || 'N/A',
          duration: courseDetails.duration || 'N/A',
          study_level: courseDetails.study_level || null,
          campus: courseDetails.campus || null,
          website: courseDetails.website || null,
          scholarship_available: courseDetails.scholarship_available || 'N/A',
          loan_offers: loan_offers || 'N/A',
          english_proficiency_required: courseDetails.english_proficiency_required || 'N/A',
          open_intakes: courseDetails.open_intakes || null,
          intake_year: courseDetails.intake_year || null,
          deadline: courseDetails.application_deadline || null, // This is key!
          application_deadline: courseDetails.application_deadline || null,
          entry_requirements: courseDetails.entry_requirements || null,
          backlog_range: courseDetails.backlog_range || null,
          cutoff_percentage: courseDetails.cutoff_percentage || null,
          test_scores: courseDetails.test_scores || [],
          scholarship_details: courseDetails.scholarship_details || null,
          remarks: courseDetails.remarks || null,
          esl_elp: courseDetails.esl_elp || null,
          job_details: courseDetails.job_details || [],
          qs_ranking: qs_ranking || 'N/A',
          vendors:  transformedLoanOffers,
          // institution_type: courseDetails.institution_type || courseDetails.type || 'Private',
          cost_of_living: courseDetails.cost_of_living || 'N/A',
          location: courseDetails.campus || courseDetails.location || country,
          university_logo: courseDetails.university_logo || null
        };
        
        console.log(`Successfully processed course ${courseId}:`, transformedCourse);
        return transformedCourse;
      } else {
        console.error(`Invalid response structure for course ${courseId}:`, response);
        return null;
      }
    }).catch((error) => {
      console.error(`Error fetching course details for ${courseId}:`, error);
      return null;
    });
}

  goBack(): void {
    if (this.puid) {
      this.router.navigate(['/results', this.puid]);
    } else {
      this.router.navigate(['/results']);
    }
  }

  viewCourseDetails(course: any): void {
    if (this.puid) {
      this.router.navigate(['/university-detail', course.courseId, this.puid]);
    } else {
      this.router.navigate(['/university-detail', course.courseId]);
    }
  }

  getLoanProviderLogo(loanProvider: string): string {
  const cleaned = loanProvider.toLowerCase().replace(/[^a-z0-9]/g, '');
  // You can use the same vendor logos or create a separate loan-logos folder
  const pngPath = `assets/vendor-logos/${cleaned}.png`;
  return pngPath;
  }

  onLoanImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    const fallbackSrc = img.src.replace('.png', '.svg');
    img.src = fallbackSrc !== img.src ? fallbackSrc : 'assets/vendor-logos/default.svg';
  }

  getLoanProviderDisplayName(loanProvider: string): string {
    // Use the same mapping as vendors since they're likely the same institutions
    const map: { [key: string]: string } = {
      auxilo: 'Auxilo',
      axisbank: 'Axis Bank',
      bankofbaroda: 'Bank of Baroda',
      prodigy: 'Prodigy',
      yesbank: 'Yes Bank',
      icicibank: 'ICICI Bank',
      credila: 'Credila',
      mpower: 'Mpower',
      idfc: 'IDFC First',
      hdfccredila: 'HDFC Credila',
      avanse: 'Avanse',
      unionbank: 'Union Bank',
      tatacapital: 'Tata Capital',
      bankofmaharashtra: 'Bank of Maharashtra',
      // Add more loan providers as needed
    };

    const key = loanProvider.toLowerCase().replace(/[^a-z0-9]/g, '');
    return map[key] || loanProvider
    ;
  }


  // Helper method to get course comparison data
  getComparisonFields(): string[] {
    return [
      'course_name',
      'university',
      'country',
      'yearly_tuition_fees',
      'currency',
      'duration',
      'application_fee',
      'scholarship_available',
      'loan_offers',
      'intake_months'
    ];
  }

  // Helper method to format currency
  formatCurrency(amount: any, currency: string): string {
    if (!amount || amount === 'N/A') return 'N/A';
    return `${currency} ${amount}`;
  }

  // Helper method to format application fee
  formatApplicationFee(fee: any, currency: string): string {
    if (!fee) return 'N/A';
    if (fee === 'Waiver' || fee === '0') return 'Waiver';
    return `${currency} ${fee}`;
  }


    // Track by function for ngFor optimization
  trackByCourseId(index: number, course: any): string {
    return course.courseId || course.course_id;
  }

  // Remove course from comparison
  removeCourse(courseId: string): void {
    this.courses = this.courses.filter(course => 
      (course.courseId || course.course_id) !== courseId
    );
    
    if (this.courses.length === 0) {
      this.goBack();
    }
  }

  // Get country flag URL
  getCountryFlag(countryName: string): string | undefined {
    const countryFlags: { [key: string]: string } = {
      'US': 'https://foreign-admits-s3-bucket-public.s3.ap-south-1.amazonaws.com/website-cms/1729754442825United%20States%20of%20America%201.png',
      'UK': 'https://foreign-admits-s3-bucket-public.s3.ap-south-1.amazonaws.com/website-cms/1729754474318UK-flag.png',
      'Canada': 'https://foreign-admits-s3-bucket-public.s3.ap-south-1.amazonaws.com/website-cms/1729754513112Canada-flag.png',
      'Germany': 'https://foreign-admits-s3-bucket-public.s3.ap-south-1.amazonaws.com/website-cms/1729754544994germany-flag.png',
      'Australia': 'https://foreign-admits-s3-bucket-public.s3.ap-south-1.amazonaws.com/website-cms/1729754573315Australia%201.png'
    };
    
    return countryFlags[countryName];
  }

  // Format date for display
  formatDate(dateString: string): string {
    if (!dateString) return 'Not specified';
    
    if (dateString.includes('Fall') || dateString.includes('Spring') || dateString.includes(':')) {
      return dateString;
    }
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString;
      }
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    } catch {
      return dateString;
    }
  }


}